/**
 * The portfolio dashboard.
 *
 * Six lenses, an S-curve, a map and the highest risks — the screen a chairman opens and, on
 * most days, the only one they open.
 *
 * The lenses come from a single request. Six independently-timed requests would let the
 * cards disagree with each other, and a dashboard whose cards contradict each other is worse
 * than a slow one.
 */
import { useQuery } from '@tanstack/react-query'
import { lazy, Suspense } from 'react'
import { useTranslation } from 'react-i18next'

import { api, ApiError } from '@/api/client'
import type { DashboardView, MapPoint } from '@/api/types'
import { navigate, type Route } from '@/app/routes'
import { Icon } from '@/components/Icon'
import { Lens } from '@/components/Lens'
import { Empty, Failed, Skeleton } from '@/components/states'
import { formatMoney } from '@/lib/format'

// The charting engine (352 kB gzipped) and the map library (218 kB) are by far the
// heaviest things in the bundle — together more than seven times the application itself.
// Both are loaded on demand so the six lenses, which are the answer an executive came for,
// paint without waiting for either.
const Curve = lazy(() => import('./Curve').then((module) => ({ default: module.Curve })))
const PortfolioMap = lazy(() =>
  import('./PortfolioMap').then((module) => ({ default: module.PortfolioMap })),
)

/**
 * Where each lens leads.
 *
 * A lens states a number and a judgement; the destination is where somebody goes to do
 * something about it. Absent from this map means the card is genuinely a statement with no
 * next step, and it renders without the affordance rather than with a dead one.
 */
const ANSWERS: Record<string, Route | undefined> = {
  delivery: { name: 'projects' },
  financial: { name: 'register', slug: 'payments' },
  risk: { name: 'register', slug: 'risks' },
  progress: { name: 'projects' },
  compliance: { name: 'periods' },
  governance: { name: 'inbox' },
}

export function Dashboard() {
  const { t, i18n } = useTranslation()

  const dashboard = useQuery({
    queryKey: ['dashboard'],
    queryFn: ({ signal }) => api.get<DashboardView>('/dashboard', signal),
  })

  const map = useQuery({
    queryKey: ['packages', 'map'],
    queryFn: ({ signal }) => api.get<MapPoint[]>('/packages/map', signal),
    // The map is supporting detail. It must never keep the six lenses off the screen.
    enabled: dashboard.isSuccess,
  })

  if (dashboard.isPending) {
    return (
      <main className="page" data-testid="dashboard">
        <Header title={t('dashboard.title')} />
        <Skeleton rows={6} />
      </main>
    )
  }

  if (dashboard.isError) {
    const error = dashboard.error
    return (
      <main className="page" data-testid="dashboard">
        <Header title={t('dashboard.title')} />
        <Failed
          message={error instanceof ApiError ? error.friendly : String(error)}
          onRetry={() => void dashboard.refetch()}
        />
      </main>
    )
  }

  const view = dashboard.data
  const totals = view.totals
  const locale = i18n.language

  return (
    <main className="page" data-testid="dashboard">
      <Header
        title={t('dashboard.title')}
        subtitle={t('dashboard.subtitle')}
        period={view.period_code ?? t('dashboard.noPeriod')}
      />

      <section className="lenses" aria-label={t('dashboard.subtitle')} data-testid="lenses">
        {view.lenses.map((lens, index) => {
          const answer = ANSWERS[lens.key]
          return (
            <Lens
              key={lens.key}
              lens={lens}
              index={index}
              // Every lens now answers "and then what?".
              //
              // `onOpen` was optional and never passed, so all six cards rendered inert
              // while carrying the whole `lens--open` treatment — hover lift, an arrow, a
              // focus ring. Six cards that look like doors and are walls. The destinations
              // only became possible once a register had a screen of its own: "344 severe"
              // leads to the risk register across the portfolio, which is the next thing
              // anybody reading that number wants.
              // `exactOptionalPropertyTypes` is on, so the prop is spread rather than
              // passed as `undefined` — omitting it is what makes the card render without
              // the affordance, and passing undefined is not the same thing.
              {...(answer ? { onOpen: () => navigate(answer) } : {})}
            />
          )
        })}
      </section>

      <section className="figures" data-testid="figures">
        <Figure label={t('dashboard.projects')} value={String(totals.projects)} />
        <Figure label={t('dashboard.packages')} value={String(totals.packages)} />
        <Figure
          label={t('dashboard.budget')}
          value={formatMoney(totals.revised_budget, locale)}
        />
        <Figure
          label={t('dashboard.certified')}
          value={formatMoney(totals.certified_to_date, locale)}
        />
      </section>

      <div className="panels">
        <section className="panel" aria-label={t('dashboard.curve')} data-testid="curve">
          <h2 className="panel__title">{t('dashboard.curve')}</h2>
          {totals.curve.length > 0 ? (
            <Suspense fallback={<Skeleton rows={4} />}>
              <Curve points={totals.curve} />
            </Suspense>
          ) : (
            <Empty title={t('dashboard.curveEmpty')} />
          )}
        </section>

        <section className="panel" aria-label={t('dashboard.topRisks')} data-testid="top-risks">
          <h2 className="panel__title">{t('dashboard.topRisks')}</h2>
          {totals.top_risks.length > 0 ? (
            <ol className="risks">
              {totals.top_risks.map((risk) => (
                <li key={risk.id} className="risks__row">
                  <span className="risks__score numeric" data-severe={(risk.risk_score ?? 0) >= 15}>
                    {risk.risk_score ?? '—'}
                  </span>
                  <span className="risks__body">
                    <span className="risks__title">{risk.title ?? risk.register_number}</span>
                    <span className="risks__ref">{risk.package_ref}</span>
                  </span>
                </li>
              ))}
            </ol>
          ) : (
            <Empty title={t('dashboard.topRisksEmpty')} />
          )}
        </section>
      </div>

      <section className="panel panel--wide" aria-label={t('dashboard.map')}>
        <h2 className="panel__title">
          {t('dashboard.map')}
          {totals.packages_unlocated > 0 ? (
            <span className="panel__note">
              {t('dashboard.mapUnlocated', { count: totals.packages_unlocated })}
            </span>
          ) : null}
        </h2>
        {map.data && map.data.length > 0 ? (
          <Suspense fallback={<Skeleton rows={2} />}>
            <PortfolioMap points={map.data} />
          </Suspense>
        ) : map.isPending ? (
          <Skeleton rows={2} />
        ) : (
          <Empty title={t('dashboard.mapEmpty')} />
        )}
      </section>
    </main>
  )
}

function Header({
  title,
  subtitle,
  period,
}: {
  title: string
  subtitle?: string
  period?: string
}) {
  const { t } = useTranslation()
  return (
    <header className="head">
      <div>
        <p className="head__eyebrow">{t('dashboard.eyebrow')}</p>
        <h1 className="head__title">{title}</h1>
        {subtitle ? <p className="head__count">{subtitle}</p> : null}
      </div>
      {period ? (
        <p className="head__period">
          <Icon name="periods" size={14} />
          <span className="head__periodLabel">{t('dashboard.period')}</span>
          <span className="numeric">{period}</span>
        </p>
      ) : null}
    </header>
  )
}

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div className="figure">
      <p className="figure__value numeric">{value}</p>
      <p className="figure__label">{label}</p>
    </div>
  )
}
